import axios, { AxiosResponse } from 'axios'
import { ContentType } from '@insided/shared/utils/contentType'
import qs from 'qs'
import { csrfToken } from '@insided/deprecated-preact-components/utils/csrfToken'

export async function createTopic(
  contentType: ContentType,
  title: string,
  content: string,
  authorId: string,
  featuredImage?: string,
  tags?: string[],
  sticky?: boolean,
  closed?: boolean,
  productAreas?: string[],
  publicLabel?: string,
  categoryId?: number,
  poll?: {
    title: string
    options: string[]
  }
): Promise<AxiosResponse> {
  const parameters = qs.stringify({ authorId }, { addQueryPrefix: true })
  return await axios.post(`/community/api/${contentType}s/create${parameters}`, {
    title,
    content,
    featuredImage,
    tags,
    sticky,
    closed,
    productAreas,
    publicLabel,
    categoryId,
    poll
  })
}

export async function publish(contentType: ContentType, privateId: number) {
  return await axios.post(`/community/api/${contentType}s/${privateId}/publish`)
}

export async function getTopic(contentType: ContentType, privateId: number) {
  const { data } = await axios.get(`/community/api/${contentType}s/${privateId}`)
  return data
}

export async function getReplies(
  contentType: ContentType,
  privateId: number,
  page?: number,
  pageSize?: number,
  sort?: string
) {
  const parameters = qs.stringify({ page, pageSize, sort }, { addQueryPrefix: true })
  const { data } = await axios.get(
    `/community/api/${contentType}s/${privateId}/replies${parameters}`
  )
  return data
}

export async function replyToTopic(
  contentType: ContentType,
  privateId: number,
  content: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/reply`, {
    content: content
  })
}

export async function like(
  contentType: ContentType,
  privateId: number,
  likedBy: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/like`, {
    likedBy
  })
}

export async function unlike(
  contentType: ContentType,
  privateId: number,
  unlikedBy: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/unlike`, {
    unlikedBy
  })
}

export async function vote(
  contentType: ContentType,
  privateId: number,
  votedBy: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/vote`, {
    votedBy
  })
}

export async function unvote(
  contentType: ContentType,
  privateId: number,
  unvotedBy: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/unvote`, {
    unvotedBy
  })
}

export async function mergeVotes(
  contentType: ContentType,
  fromPrivateId: number,
  toPrivateId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${fromPrivateId}/mergeVotes`, {
    toIdeaId: toPrivateId
  })
}

export async function assignIdeaStatus(
  contentType: ContentType,
  privateId: number,
  ideaStatusId: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/assignIdeaStatus`, {
    ideaStatusId: ideaStatusId
  })
}

export async function assignModerator(
  contentType: ContentType,
  privateIds: number[],
  moderatorId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/assignModerator`, {
    ideaIds: privateIds,
    assignedTo: moderatorId
  })
}

export async function unassignModerator(
  contentType: ContentType,
  privateIds: number[]
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/unassignModerator`, {
    ideaIds: privateIds
  })
}

export async function editTitle(
  contentType: ContentType,
  privateId: number,
  title: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editTitle`, {
    title
  })
}

export async function editContent(
  contentType: ContentType,
  privateId: number,
  content: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editContent`, {
    content
  })
}

export async function editPollTitle(
  contentType: ContentType,
  privateId: number,
  title: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editPollTitle`, {
    title
  })
}

export async function editPollOptions(
  contentType: ContentType,
  privateId: number,
  options: string[]
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editPollOptions`, {
    options
  })
}

export async function editReply(
  contentType: ContentType,
  replyId: number,
  content: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/replies/${replyId}/editContent`, {
    content
  })
}

export async function deletePoll(
  contentType: ContentType,
  privateId: number
): Promise<AxiosResponse> {
  return await axios.delete(`/community/api/${contentType}s/${privateId}/poll`, {
    data: { csrfToken: csrfToken() }
  })
}

export async function editPublicLabel(
  contentType: ContentType,
  privateId: number,
  publicLabel: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editPublicLabel`, {
    publicLabel
  })
}

export async function editTags(
  contentType: ContentType,
  privateId: number,
  tags: string[]
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editTags`, {
    tags
  })
}

export async function editModeratorTags(
  contentType: ContentType,
  privateId: number,
  moderatorTags: string[]
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editModeratorTags`, {
    moderatorTags
  })
}

export async function editFeaturedImage(
  contentType: ContentType,
  privateId: number,
  featuredImage: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editFeaturedImage`, {
    featuredImage
  })
}

export async function editProductAreas(
  contentType: ContentType,
  privateId: number,
  productAreas: string[]
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editProductAreas`, {
    productAreas
  })
}

export async function changeAuthor(
  contentType: ContentType,
  privateId: number,
  authorId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/changeAuthor`, {
    authorId
  })
}

export async function toggleClosed(
  contentType: ContentType,
  privateId: number,
  closed: boolean
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/toggleClosed`, {
    closed
  })
}

export async function toggleSticky(
  contentType: ContentType,
  privateId: number,
  sticky: boolean
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/toggleStickyState`, {
    sticky
  })
}

export async function toggleTrashed(
  contentType: ContentType,
  privateId: number,
  trashed: boolean
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/toggleTrashed`, {
    trashed
  })
}

export async function move(
  contentType: ContentType,
  privateId: number,
  categoryId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/move`, {
    categoryId
  })
}

export async function schedule(
  contentType: ContentType,
  privateId: number,
  scheduleDate: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/schedule`, {
    scheduleDate
  })
}

export async function cancelSchedule(
  contentType: ContentType,
  privateId: number
): Promise<AxiosResponse> {
  return await axios.delete(`/community/api/${contentType}s/${privateId}/schedule`, {
    data: { csrfToken: csrfToken() }
  })
}

export async function deleteTopic(
  contentType: ContentType,
  privateId: number
): Promise<AxiosResponse> {
  return await axios.delete(`/community/api/${contentType}s/${privateId}`, {
    data: { csrfToken: csrfToken() }
  })
}

export async function setModerationLabel(
  contentType: ContentType,
  privateIds: number[],
  moderationLabelId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/setModerationLabel`, {
    [`${contentType}Ids`]: privateIds,
    moderationLabelId
  })
}

export async function unsetModerationLabel(
  contentType: ContentType,
  privateIds: number[]
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/unsetModerationLabel`, {
    [`${contentType}Ids`]: privateIds
  })
}

export async function approveTopic(
  contentType: ContentType,
  privateId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/approve`)
}

export async function approveReply(
  contentType: ContentType,
  replyId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/replies/${replyId}/approve`)
}

export async function getPrivateId(publicId: number) {
  const {
    data: { privateId }
  } = await axios.get(`/topic/privateId/${publicId}`)
  return privateId
}

export async function getContentType(publicId: number) {
  const {
    data: { contentType }
  } = await axios.get(`/topic/contentType?publicId=${publicId}`)
  return contentType
}

export async function isTopicAvailable(privateId: number, contentType: string) {
  const {
    data: { isTopicAvailable }
  } = await axios.get(`/topic/isTopicAvailable?privateId=${privateId}&contentType=${contentType}`)
  return isTopicAvailable
}
